import {PHASES, WAR_PHASE_STAGES} from "../../constants/phases";
import {getPhaseDurationMs} from "./getPhaseDurationMs";
import {checkIfParallelTurn} from "../gameState/turn/checkIfParallelTurn";
import {checkIfParallelSupplyPhase, getNotCompletedPlayers} from "../gameState/supply/parallelSupply";
import {checkIfPlayerConfirmedSetupPhase} from "../gameState/setup/checkIfPlayerConfirmedSetupPhase";

/**
 * ПЕРСОНАЛЬНОЕ ВРЕМЯ ХОДА.
 *
 * Обычно время хода одно на всех и зависит только от фазы/этапа
 * (getPhaseDurationMs). В промо-матче против LLM-агента люди ходят по
 * стандартному времени, а агенту нужно больше: один его ход — это цепочка
 * вызовов инструментов с обдумыванием, и стандартные 40 с он не успевает.
 * Поэтому сервер кладет агенту в START_GAME персональное время
 * gameState.playerTurnTimeOverrides = {[playerId]: {durationMs, isActive}} —
 * предохранитель до AI_TURN_CAP_MS на каждый этап. Пока за агента играет
 * серверный бот (автопилот), сервер выключает время флагом isActive: false, и
 * бот ходит по стандартному времени.
 *
 * В обычных матчах поля нет (или оно пустое) — и все здесь сводится ровно к
 * getPhaseDurationMs, как было до него.
 *
 * На поочередном этапе ход один — у текущего игрока, и его время и есть время
 * хода. На одновременном (расстановка, ближний бой, одновременное снабжение)
 * действуют все разом, и дедлайнов два: людей сервер завершает по
 * стандартному времени, а игроков с увеличенным временем — по их собственному
 * (getTurnDeadlines). Клиенту показываем его собственное время, пока он сам
 * должен действовать, а закончившему и зрителю — сколько еще ждать всех.
 *
 * Все времена здесь — те, что видит игрок. Сервер закрывает этап по таймеру
 * чуть позже каждого из них — на отсрочку TURN_AUTO_END_GRACE_MS (см. ниже).
 */

// Предохранитель хода агента: дольше этого один этап не тянется
export const AI_TURN_CAP_MS = 10 * 60 * 1000;

/**
 * ОТСРОЧКА СЕРВЕРНОГО АВТОЗАВЕРШЕНИЯ.
 *
 * Сервер закрывает этап по таймеру не ровно в срок, который видит игрок, а на
 * TURN_AUTO_END_GRACE_MS позже. Без отсрочки действие последнего момента (клик
 * на исходе своего таймера) доезжало до сервера уже после закрытия хода: сервер
 * его отвергал и досылал отправителю полное состояние — оверлей и полная
 * перерисовка поля (см. shouldRejectStaleRelayedAction). За отсрочку такое
 * действие успевает доехать и применяется как обычное.
 *
 * Отсрочка — невидимый запас сервера, а не время хода. В длительности
 * (getTurnDurationMs, getTurnDeadlines), а значит в таймер клиента, сторож
 * протухшего состояния и текст для MCP-агента она не входит: иначе игрок видел
 * бы лишнюю секунду и снова кликал бы в самый момент закрытия. Применяет ее
 * только серверный планировщик таймеров (scheduleLobbyTurnTimer в
 * server/index.js). Локальная игра закрывает ход ровно в срок (LocalTurnTimer):
 * там действию ехать некуда.
 *
 * Со своей стороны клиент после нуля на таймере ввод игрока не принимает (см.
 * utils/gameState/turn/turnInputLock): иначе тот, кто продолжает кликать и
 * после нуля, попадал бы действием уже в конец отсрочки. Так вся отсрочка
 * остается на доставку отправленного до нуля.
 */
export const TURN_AUTO_END_GRACE_MS = 1000;

/**
 * Через сколько серверу закрывать этап по таймеру: до срока, который видит
 * игрок, осталось msUntilDeadline, плюс отсрочка. Отрицательный остаток — срок
 * уже прошел (таймер перезаводится посреди этапа): пока идет отсрочка, этап
 * закрывается по ее концу, а прошла и она — сразу.
 *
 * @param {number} msUntilDeadline - сколько осталось до срока игрока
 * @param {number} [graceMs] - отсрочка сервера
 * @returns {number} задержка таймера, не меньше нуля
 */
export const getTurnAutoEndDelayMs = (msUntilDeadline, graceMs = TURN_AUTO_END_GRACE_MS) => Math.max(
  0,
  msUntilDeadline + graceMs
);

/**
 * ОКНО ОБЛЕТА ЗАХВАЧЕННЫХ ГОРОДОВ.
 *
 * В конце раунда клиенты показывают захваты облетом камеры (см.
 * scenes/CityCaptureAnimation), и пока он идет, игрок действовать не может.
 * Чтобы облет не съедал время хода, захват кладет в состояние окно
 * gameState.cityCaptureTimeWindow = {startsAt, durationMs} (см. редьюсер
 * CAPTURE_CITIES; startsAt — серверное время захвата, длительность — по числу
 * захватов, getCityCaptureAnimationDurationMs). Этап, начавшийся внутри окна
 * (turnEndTimestamp не раньше startsAt и раньше конца окна), получает добавку
 * на остаток окна к своему времени — и в таймере, и в блокировке ввода, и в
 * таймере сервера, и в тексте MCP-агента. Правило одно для всех и зависит
 * только от состояния: обычно это первый этап нового раунда, а если он
 * закрылся мгновенно (авто-завершение пустого снабжения), остаток окна
 * переходит к следующему.
 *
 * Тем же окном сервер и клиент-делегат придерживают ходы ботов
 * (getCityCaptureAnimationRemainingMs): иначе их действия шли бы за кадром.
 */
export const getCityCaptureStageExtraMs = (gameState) => {
  const captureWindow = gameState?.cityCaptureTimeWindow;
  const stageStartedAt = gameState?.turnEndTimestamp;
  if (!captureWindow || !Number.isFinite(stageStartedAt) || !Number.isFinite(captureWindow.startsAt)
    || !Number.isFinite(captureWindow.durationMs) || captureWindow.durationMs <= 0) {
    return 0;
  }
  if (stageStartedAt < captureWindow.startsAt) {
    return 0;
  }
  return Math.max(0, captureWindow.startsAt + captureWindow.durationMs - stageStartedAt);
};

/**
 * Сколько еще идет облет захваченных городов на текущем этапе, если смотреть
 * из момента nowMs (серверное время): 0 — окна нет или оно кончилось
 */
export const getCityCaptureAnimationRemainingMs = (gameState, nowMs) => {
  if (getCityCaptureStageExtraMs(gameState) <= 0) {
    return 0;
  }
  const {startsAt, durationMs} = gameState.cityCaptureTimeWindow;
  return Math.max(0, startsAt + durationMs - nowMs);
};

/**
 * Стандартное время хода текущей фазы/этапа — одно для всех игроков. Этапу,
 * начавшемуся во время облета захваченных городов, — с добавкой на облет
 * (getCityCaptureStageExtraMs)
 */
export const getStandardTurnDurationMs = (gameState) => {
  const phaseDurationMs = getPhaseDurationMs(
    gameState?.phase,
    gameState?.warPhaseStage,
    gameState?.fastMode
  );
  return phaseDurationMs ? phaseDurationMs + getCityCaptureStageExtraMs(gameState) : phaseDurationMs;
};

/**
 * Включенное персональное время игрока или null. Запись без положительного
 * конечного durationMs не учитывается: кривое значение из состояния не должно
 * обнулить таймер или сделать ход бесконечным — тогда лучше стандартное время
 */
export const getActiveTurnTimeOverrideMs = (gameState, playerId) => {
  if (!playerId) {
    return null;
  }
  const override = gameState?.playerTurnTimeOverrides?.[playerId];
  if (!override || !override.isActive) {
    return null;
  }
  const {durationMs} = override;
  return Number.isFinite(durationMs) && durationMs > 0 ? durationMs : null;
};

/**
 * Время хода игрока на текущем этапе: включенное персональное (оно не зависит
 * от быстрого режима — это предохранитель в абсолютных минутах) или
 * стандартное время фазы.
 *
 * У этапа без таймера (подготовка поля, война без этапа — getPhaseDurationMs
 * там не число) персональное время таймер не заводит: оно продлевает отсчет, а
 * не создает его там, где его нет ни у кого
 */
export const getPlayerTurnDurationMs = (gameState, playerId) => {
  const standardMs = getStandardTurnDurationMs(gameState);
  if (!standardMs) {
    return standardMs;
  }
  const overrideMs = getActiveTurnTimeOverrideMs(gameState, playerId);
  // Облет захваченных городов идет и у агента — добавка и к его времени
  return overrideMs !== null ? overrideMs + getCityCaptureStageExtraMs(gameState) : standardMs;
};

/**
 * Кто еще должен действовать на одновременном этапе, или null, если этап
 * поочередной (тогда ходит только текущий игрок).
 *
 * Правила «кто закончил» те же, что у сервера и кнопки завершения хода:
 *  - расстановка — кто не подтвердил стартовый квартал;
 *  - ближний бой — участники боев, еще не подтвердившие этап. Общего
 *    помощника на это нет (getLeftPlayersPendingCombatConfirmation считает
 *    только ушедших и по корневому состоянию), поэтому правило повторено
 *    здесь: ждущие минус подтвердившие;
 *  - одновременное снабжение — кто не закончил свою часть фазы, в порядке
 *    хода. Партия, закончившаяся на входе в снабжение, фазу уже не играет
 *    (checkIfParallelSupplyPhase) — ждать в ней некого.
 *
 * @returns {string[]|null}
 */
export const getParallelStagePendingPlayerIds = (gameState) => {
  if (!gameState) {
    return null;
  }
  const {phase, warPhaseStage} = gameState;
  if (!checkIfParallelTurn(phase, warPhaseStage, gameState.isParallelSupply)) {
    return null;
  }

  if (phase === PHASES.SETUP) {
    const confirmedIds = gameState.playersConfirmedSetupPhase || [];
    return (gameState.players || [])
      .filter((player) => !checkIfPlayerConfirmedSetupPhase(confirmedIds, player))
      .map((player) => player.id);
  }

  if (phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.COMBAT) {
    const confirmedIds = new Set(gameState.playersConfirmedCombatStage || []);
    return (gameState.playersWaitedToConfirmCombatStage || [])
      .filter((playerId) => !confirmedIds.has(playerId));
  }

  // Остается одновременное снабжение
  if (!checkIfParallelSupplyPhase(gameState)) {
    return [];
  }
  return getNotCompletedPlayers(gameState).map((player) => player.id);
};

/**
 * Кто еще должен действовать на текущем этапе: на одновременном — список
 * getParallelStagePendingPlayerIds, на поочередном — текущий игрок
 */
export const getTurnPendingPlayerIds = (gameState) => {
  const parallelPendingIds = getParallelStagePendingPlayerIds(gameState);
  if (parallelPendingIds !== null) {
    return parallelPendingIds;
  }
  const currentPlayerId = gameState?.players?.[gameState?.currentPlayerIndex]?.id;
  return currentPlayerId ? [currentPlayerId] : [];
};

// Самое долгое время среди тех, кого еще ждут; никого не ждут — стандартное
const getMaxPendingTurnDurationMs = (gameState, pendingIds) => {
  if (pendingIds.length === 0) {
    return getStandardTurnDurationMs(gameState);
  }
  return Math.max(...pendingIds.map((playerId) => getPlayerTurnDurationMs(gameState, playerId)));
};

/**
 * Время хода, которое показывать (кнопка завершения хода, таймер, MCP).
 *
 * Поочередной этап — время текущего игрока, кто бы ни смотрел: агенту все
 * видят его 10 минут, а не стандартный отсчет, который «истечет» на глазах без
 * всякого завершения хода.
 *
 * Одновременный этап — свое время, пока зритель сам должен действовать (люди
 * торопятся по стандартному таймеру, даже если агент думает дольше); закончил
 * или вообще не игрок — сколько ждать последнего из оставшихся.
 *
 * @param {Object} gameState - общее состояние партии (state.shared.gameState)
 * @param {{viewerId?: string}} [options] - игрок, для которого считаем
 * @returns {number}
 */
export const getTurnDurationMs = (gameState, {viewerId} = {}) => {
  const parallelPendingIds = getParallelStagePendingPlayerIds(gameState);
  if (parallelPendingIds === null) {
    return getPlayerTurnDurationMs(gameState, gameState?.players?.[gameState?.currentPlayerIndex]?.id);
  }
  if (viewerId && parallelPendingIds.includes(viewerId)) {
    return getPlayerTurnDurationMs(gameState, viewerId);
  }
  return getMaxPendingTurnDurationMs(gameState, parallelPendingIds);
};

/**
 * Сколько времени у игрока на текущем этапе, пока от него еще ждут действий,
 * или null, если сейчас действовать не ему: чужой ход, он уже подтвердил или
 * закончил свою часть одновременного этапа, он зритель, партия не идет, у этапа
 * нет таймера.
 *
 * Это тот срок, который игрок видит на своей кнопке завершения хода и по
 * которому сервер завершает ИМЕННО его (в промо-матче против ИИ люди — по
 * стандартному времени, агент — по своему). По нему клиент перестает принимать
 * ввод игрока, когда время вышло (см. utils/gameState/turn/turnInputLock).
 *
 * @param {Object} gameState - общее состояние партии
 * @param {string} playerId - действующий игрок: сам пользователь или бот,
 *   которого ведет админ
 * @returns {number|null}
 */
export const getPlayerOwnTurnDurationMs = (gameState, playerId) => {
  if (!playerId || !gameState?.isStarted || gameState.gameResults?.length > 0) {
    return null;
  }
  if (!getTurnPendingPlayerIds(gameState).includes(playerId)) {
    return null;
  }
  const durationMs = getTurnDurationMs(gameState, {viewerId: playerId});
  return Number.isFinite(durationMs) && durationMs > 0 ? durationMs : null;
};

/**
 * Дедлайны текущего этапа для сервера (отсчет от turnEndTimestamp). Это сроки,
 * которые видят игроки; свой таймер сервер ставит на отсрочку позже каждого
 * (getTurnAutoEndDelayMs).
 *
 *  - standardMs — стандартное время фазы: к нему сервер завершает всех, кого
 *    ждут, кроме extendedPendingIds;
 *  - finalMs — когда этап закрывается окончательно: самое долгое время среди
 *    тех, кого еще ждут (никого — стандартное). Без персональных времен
 *    finalMs === standardMs, и второй таймер не нужен;
 *  - extendedPendingIds — кого ждут дольше стандартного (включенное
 *    персональное время больше стандартного); их сервер завершает в finalMs.
 *
 * На поочередном этапе «ждут» только текущего игрока.
 *
 * @returns {{standardMs: number, finalMs: number, extendedPendingIds: string[]}}
 */
export const getTurnDeadlines = (gameState) => {
  const standardMs = getStandardTurnDurationMs(gameState);
  const pendingIds = getTurnPendingPlayerIds(gameState);
  const extendedPendingIds = pendingIds.filter((playerId) => {
    const overrideMs = getActiveTurnTimeOverrideMs(gameState, playerId);
    return overrideMs !== null && getPlayerTurnDurationMs(gameState, playerId) > standardMs;
  });
  return {
    standardMs,
    finalMs: getMaxPendingTurnDurationMs(gameState, pendingIds),
    extendedPendingIds,
  };
};
